import { readSession, rejectSession, renewSession } from '../auth/session';

/**
 * Cliente do serviço de monitoramento (@sicop/monitor).
 *
 * As chamadas são same-origin: `/api/monitor/*` é servido pelo próprio monitor
 * (que também entrega esta tela) e encaminhado pelo Vite em desenvolvimento.
 *
 * Toda chamada leva o **token de sessão do SICOP** em `Authorization: Bearer`
 * — emitido no login (`createSession`), com 15 minutos de validade e conferido
 * no banco pelo monitor (ver `packages/monitor/src/http/auth.ts`). Usar a tela
 * renova a sessão: o prazo vigente volta em um cabeçalho a cada resposta. É do
 * servidor também que sai o autor da auditoria — a tela não diz mais quem é.
 */
const BASE_URL = '/api/monitor';

/** `unknown` = cadastrada, mas o cron ainda não checou. */
export type HealthStatus = 'up' | 'down' | 'unknown';

/** `partial` = pelo menos uma API fora, mas não todas (amarelo no cabeçalho). */
export type OverallStatus = 'ok' | 'partial' | 'down' | 'unknown';

export interface OperatorOption {
  code: string;
  name: string;
}

export interface ApiEndpoint {
  id: number;
  name: string;
  url: string;
  method: string;
  active: boolean;
  timeoutMs: number;
  operator: OperatorOption | null;
  health: {
    status: HealthStatus;
    httpStatus: number | null;
    latencyMs: number | null;
    error: string | null;
    checkedAt: string | null;
  };
}

export interface HealthSummary {
  status: OverallStatus;
  total: number;
  up: number;
  down: number;
  unknown: number;
  checkedAt: string | null;
}

export interface ApisState {
  apis: ApiEndpoint[];
  summary: HealthSummary;
  /** Cadência do cron no servidor, em minutos (vem de HEALTH_CHECK_*). */
  intervalMinutes: number;
}

/** Contagem de um card do dashboard, quebrada por operadora. */
export interface OperatorCount {
  code: string;
  name: string;
  /** O número grande do card. */
  active: number;
  /** Total incluindo o inativo, usado no subtítulo. */
  total: number;
}

export interface MasterCount extends OperatorCount {
  accounts: number;
}

export type ServiceRunStatus = 'running' | 'ok' | 'error';

export interface ServiceRun {
  id: number;
  /** Serviço dono do job (eagle, hawk, monitor). */
  service: string;
  job: string;
  /** Nome de tela do job, resolvido pelo servidor. */
  label: string;
  status: ServiceRunStatus;
  /** `queue` é a rodada que nasceu de uma publicação de outro sistema. */
  triggeredBy: 'cron' | 'manual' | 'queue';
  processed: number;
  failed: number;
  durationMs: number | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface DashboardState {
  masters: MasterCount[];
  lines: OperatorCount[];
  /** A última execução de cada job, da mais recente para a mais antiga. */
  serviceRuns: ServiceRun[];
}

/** Um job agendado do SICOP, com a última rodada que ele teve. */
/**
 * O que faz a rodada acontecer. `cron` tem horário e próxima execução; `queue`
 * depende de outro sistema publicar, e por isso não tem nem uma coisa nem outra.
 */
export type JobKind = 'cron' | 'queue';

export interface ScheduledJob {
  /** Serviço que executa a rodada (eagle, hawk, monitor). */
  service: string;
  /** Slug estável do job; é o que o filtro do histórico manda para o servidor. */
  job: string;
  label: string;
  description: string;
  kind: JobKind;
  /** Expressão cron do ambiente; nula quando a rodada vem de uma fila. */
  schedule: string | null;
  timezone: string;
  scheduled: boolean;
  /** Por que não está agendado neste ambiente; nulo quando está. */
  unscheduledReason: string | null;
  /** Nulo quando o job não está agendado ou é disparado por publicação. */
  nextRunAt: string | null;
  /** Se a tela pode disparar a rodada na hora. */
  triggerable: boolean;
  /**
   * Se há uma rodada em andamento *de verdade*. O servidor descarta as linhas
   * presas em execução por um serviço que morreu no meio — sem isso, um
   * reinício travaria o botão para sempre.
   */
  running: boolean;
  /** Nulo enquanto o job nunca rodou. */
  lastRun: ServiceRun | null;
}

export interface JobsState {
  jobs: ScheduledJob[];
  /** Fuso em que os agendamentos são interpretados no servidor. */
  timezone: string;
}

export interface ServiceRunsPage {
  runs: ServiceRun[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface RunsQuery {
  /** Slug do job; nulo lista todos. */
  job: string | null;
  status: ServiceRunStatus | null;
  page: number;
}

export interface NewApiEndpoint {
  name: string;
  url: string;
  operatorCode: string | null;
}

/** Uma linha da listagem de Linhas. */
export interface LineNumber {
  /**
   * `numbers.id`, que a sincronização usa. Nulo para a linha que a operadora
   * cobra e o cadastro não tem — ela aparece com o consumo da importação
   * diária, mas não dá para sincronizar.
   */
  id: number | null;
  /** Só dígitos, com DDD; a tela formata para exibir. */
  line: string;
  name: string | null;
  active: boolean;
  operationStatus: string | null;
  operator: OperatorOption;
  /** Nulo quando a conta ainda não tem vínculo ativo com um master. */
  master: string | null;
  account: string;
  /** Nulo quando a última importação de consumo não trouxe esta linha. */
  consumption: LineConsumption | null;
  /** Situação no SGC: com cliente, sem cliente (estoque) ou inativa. */
  situation: LineSituation;
  /** Por que a linha não está ativa (ex.: "cancelada no SGC em 30/09/2026"); nulo quando está. */
  situationReason: string | null;
  lastCallsCheck: string | null;
}

/** Uma fatia da quebra por grupo de classificação (só a Claro grava). */
export interface LineSegmentConsumption {
  key: string;
  consumed: number | null;
}

/** O detalhe da linha para o side sheet: a linha + CNPJ, empresa e segmentos. */
export interface LineDetail extends LineNumber {
  /** Identificador que a importação gravou (CPF/CNPJ do titular). */
  identifier: string | null;
  /** Empresa/cliente no portal (idcustomer na Claro); nulo na Vivo. */
  company: string | null;
  /** CNPJ da master dona da linha. */
  masterCnpj: string | null;
  /** Quebra por grupo de classificação; vazio na Vivo. */
  segments: LineSegmentConsumption[];
}

export interface LineConsumption {
  /**
   * Os dois faltam de forma independente. A franquia falta quando nem o
   * contrato (SGC) nem a operadora a informaram — plano compartilhado ou linha
   * sem pacote de dados; a linha continua tendo consumido o que consumiu.
   */
  consumed: number | null;
  /** Nulo quando não se sabe — e também quando o que se tem é zero. */
  hired: number | null;
  /** Consumo sobre o contratado no SGC (`50.0%`); nulo sem contrato. */
  percentage?: string | null;
  /** De onde veio `hired`: o contrato do SGC ou, sem ele, a cota do portal (Vivo). */
  hiredSource?: 'sgc' | 'portal' | null;
  /**
   * O SGC respondeu que não conhece a linha e não há outra franquia para
   * mostrar. Opcional porque a resposta da sincronização não o traz.
   */
  notFoundInSgc?: boolean;
  /** Nome do plano no relatório da operadora. */
  service: string | null;
  /** Quando esta leitura entrou no banco — é a idade do dado na tela. */
  updatedAt: string | null;
}

/**
 * De onde veio o consumo devolvido pela sincronização: `operator` quando a
 * operadora foi consultada na hora (Vivo), `database` quando o número é o da
 * importação diária (Claro).
 */
export type ConsumptionSource = 'operator' | 'database';

export interface LineSyncResult {
  number: LineNumber;
  source: ConsumptionSource;
}

/* ── Notificações ──────────────────────────────────────────────────────── */

/** `job` = rodada de serviço; `line_sync` = linha sincronizada; `report` = relatório. */
export type NotificationKind = 'job' | 'line_sync' | 'report';

/** Como o fato terminou — é o que dá a cor do aviso. */
export type NotificationStatus = 'ok' | 'error';

export interface AppNotification {
  id: number;
  kind: NotificationKind;
  status: NotificationStatus;
  title: string;
  body: string;
  /** Serviço que emitiu (monitor, eagle, hawk). */
  service: string;
  read: boolean;
  createdAt: string;
}

export interface NotificationsState {
  notifications: AppNotification[];
  /** O número do sino. */
  unread: number;
}

export interface NumbersPage {
  numbers: LineNumber[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Uma conta de portal na listagem de Contas. */
export interface Account {
  id: number;
  /** Login no portal: CPF do titular na Claro, id da conta na Vivo. */
  username: string;
  active: boolean;
  operator: OperatorOption;
  /** Nulo enquanto a sincronização com o SGC não casar a conta com um master. */
  master: string | null;
  company: string | null;
  /** Caixa que recebe o OTP; só a Claro usa. */
  otpEmail: string | null;
  /** Só se o token existe — o valor nunca sai do servidor. */
  hasOtpAppPassword: boolean;
  lines: { total: number; active: number };
  createdAt: string;
}

/** O ciclo de faturamento vigente de uma master numa operadora. */
export interface MasterCycle {
  operatorCode: string;
  operatorName: string;
  /** Dia em que o ciclo fecha; abre no seguinte. */
  billEndDay: number;
  contractNumber: string | null;
  sgcContractId: number | null;
  /** `sgc` (carregado do SGC) ou `manual` (editado na tela). */
  source: 'sgc' | 'manual';
}

/** Uma master na tela de Masters. */
export interface Master {
  id: number;
  /** `sgc` (snapshot do SGC), `sicop-local` (cadastrada aqui) ou `legacy_hawk`. */
  source: string;
  externalId: number;
  name: string;
  cnpj: string;
  code: string | null;
  brandExternalId: number | null;
  active: boolean;
  syncedAt: string | null;
  updatedAt: string;
  accountsCount: number;
  cycles: MasterCycle[];
}

/** O que a tela manda de cada ciclo; `billEndDay` nulo remove o ciclo da operadora. */
export interface MasterCycleInput {
  operatorCode: string;
  billEndDay: number | null;
  contractNumber?: string | null;
}

export interface NewMaster {
  name: string;
  cnpj: string;
  code: string;
  cycles: MasterCycleInput[];
}

/** Edição de master: ausente = não mexer; `code` vazio apaga o código. */
export interface MasterChanges {
  name?: string;
  cnpj?: string;
  code?: string;
  active?: boolean;
  cycles?: MasterCycleInput[];
}

/** Filtro de situação da tela de Contas. */
export type AccountStatusFilter = 'active' | 'inactive' | 'all';

/**
 * Edição de uma conta. Campo vazio ou ausente = não mexer: a senha e o token
 * nunca chegam à tela, então ela não tem como reenviá-los.
 */
export interface AccountChanges {
  password?: string;
  otpEmail?: string;
  otpAppPassword?: string;
  active?: boolean;
}

export interface NewAccount {
  operatorCode: string;
  username: string;
  password: string;
  /** Só para operadora com OTP; nulo nas demais. */
  otpEmail: string | null;
  otpAppPassword: string | null;
}

/**
 * Situação da linha, como o SGC a vê: `active` com cliente, `unassigned` sem
 * cliente (estoque), `inactive` cancelada, inexistente no SGC ou fora do portal.
 */
export type LineSituation = 'active' | 'unassigned' | 'inactive';

/** Filtro de situação da tela de Linhas: uma situação ou todas. */
export type LineStatusFilter = LineSituation | 'all';

/** Segmento de consumo da Claro (a quebra gravada em `line_data_consumption_groups`). */
export type LineSegment = 'web' | 'whatsapp' | 'social' | 'mobility' | 'roaming';

/** Rótulos PT-BR dos segmentos; valor sem tradução volta como veio. */
const SEGMENT_LABELS: Record<string, string> = {
  web: 'Web',
  whatsapp: 'WhatsApp',
  social: 'Social',
  mobility: 'Mobilidade',
  roaming: 'Roaming',
};

/** Rótulo para a tela; cai no valor cru quando o grupo não tem tradução. */
export function segmentLabel(key: string): string {
  return SEGMENT_LABELS[key] ?? key;
}

/** Os segmentos oferecidos no filtro da tela de Linhas. */
export const LINE_SEGMENTS: Array<{ value: LineSegment; label: string }> = [
  { value: 'web', label: 'Web' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'social', label: 'Social' },
  { value: 'mobility', label: 'Mobilidade' },
  { value: 'roaming', label: 'Roaming' },
];

export interface NumbersQuery {
  /** `operators.code`; nulo lista todas. */
  operatorCode: string | null;
  /** Busca livre: pelo número ou pelo nome do master, como o usuário digitou. */
  line: string;
  /** Nomes de master (busca parcial no servidor, OU entre eles); vazio lista todos. */
  masters: string[];
  status: LineStatusFilter;
  /** Segmentos Claro cujo consumo a lista mostra; vazio = consumo total. */
  groups: string[];
  page: number;
}


/* ── Relatórios de consumo ─────────────────────────────────────────────── */

/** Master oferecido no formulário: só os que têm conta ativa. */
export interface ReportMaster {
  id: number;
  name: string;
  cnpj: string;
  accounts: number;
  /** Linhas ativas — é o que dimensiona a espera da geração. */
  lines: number;
  /** `operators.code` das contas do master. */
  operators: string[];
}

/** Conta de um master, no segundo campo do formulário. */
export interface ReportAccount {
  id: number;
  username: string;
  operator: OperatorOption;
  accountNumber: string | null;
  lines: number;
}

export interface BillingPeriod {
  /** `YYYY-MM-DD`. */
  start: string;
  end: string;
  /** `10/06 – 09/07/2026`. */
  label: string;
  /** O ciclo em andamento. */
  current: boolean;
}

/**
 * De onde o relatório tira o consumo.
 *
 * `api` pergunta linha a linha à operadora — vale para as duas e lê o ciclo em
 * andamento. `vivo_portal_last3` baixa do portal da Vivo o XLS de cada conta,
 * com os últimos ciclos: ordens de grandeza mais rápido, mas só existe lá e o
 * período quem define é o arquivo.
 */
export type ReportSource = 'api' | 'vivo_portal_last3';

export interface ReportSourceOption {
  id: ReportSource;
  name: string;
  /** `operators.code` a que a origem se restringe; nulo quando serve a todas. */
  operator: string | null;
  description: string;
}

export interface ReportOptions {
  masters: ReportMaster[];
  periods: BillingPeriod[];
  sources: ReportSourceOption[];
  /**
   * Falso quando o servidor não fala com o SGC. O relatório ainda sai, mas a
   * franquia vem da cota do portal da operadora em vez do contrato — que é o
   * número menos preciso dos dois.
   */
  franchiseConfigured: boolean;
}

export type ReportStatus = 'pending' | 'running' | 'ready' | 'error';

export interface ConsumptionReport {
  id: number;
  status: ReportStatus;
  master: string;
  /** Nulo quando o relatório cobre todas as contas do master. */
  account: string | null;
  accountOperator: string | null;
  /** Nulo quando cobre todas as linhas. */
  line: string | null;
  periodStart: string;
  periodEnd: string;
  totalLines: number;
  /** Linhas que a operadora não respondeu; entram na planilha sem consumo. */
  failedLines: number;
  /** Soma das franquias das linhas, em GB; nulo enquanto a coleta não terminou. */
  totalHiredGb: number | null;
  totalConsumedGb: number | null;
  error: string | null;
  requestedBy: string | null;
  source: ReportSource;
  createdAt: string;
  finishedAt: string | null;
}

/** O andamento de uma conta dentro de um relatório do portal. */
export interface ReportAccountProgress {
  accountId: number | null;
  username: string;
  accountNumber: string | null;
  status: 'pending' | 'ok' | 'error';
  rowCount: number;
  fileName: string | null;
  error: string | null;
  downloadedAt: string | null;
}

export interface NewReport {
  masterId: number;
  /** Nulo = todas as contas do master. */
  accountId: number | null;
  /** Vazio = todas as linhas. */
  line: string;
  /** Ignorados quando a origem é o portal: lá o período vem do arquivo. */
  periodStart: string;
  periodEnd: string;
  requestedBy: string | null;
  source: ReportSource;
}

/* ── Auditoria ─────────────────────────────────────────────────────────── */

/** Quem entrou pelo Google, como o SICOP o guarda. */
export interface SessionUser {
  id: number;
  email: string;
  /** Nulo quando o Google não devolveu o nome. */
  name: string | null;
  /** Hoje `admin` para todo mundo; a separação de permissão ainda não existe. */
  role: 'admin' | 'viewer';
  /** Foto do perfil Google, como o SICOP a guardou no último login. */
  pictureUrl: string | null;
  lastLoginAt: string | null;
}

/** Uma opção do filtro de usuário da tela de Auditoria. */
export interface AuditUser {
  id: number;
  /** Nome do Google, ou o e-mail quando ele não veio. */
  name: string;
  email: string;
}

export interface AuditEntry {
  id: number;
  /** Nulo quando o autor não estava cadastrado, ou foi removido depois. */
  userId: number | null;
  /** Nome de exibição de quem fez, já resolvido pelo servidor. */
  actor: string;
  actorEmail: string | null;
  /** Slug estável da ação (`report.request`, `job.run`…). */
  action: string;
  /** A frase da lista: "gerou relatório de consumo". */
  actionLabel: string;
  /** Sobre o quê: o master e o período, o número da linha, a URL. */
  target: string | null;
  createdAt: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AuditQuery {
  /** `users.id`; nulo lista todos. */
  userId: number | null;
  /** Bordas do período em ISO; nulas = sem corte. */
  from: string | null;
  to: string | null;
  page: number;
}

/**
 * A credencial da chamada.
 *
 * Lida da sessão a cada chamada, e não guardada no módulo: um token em cache
 * continuaria sendo mandado depois de a sessão expirar ou de alguém sair.
 */
function authHeader(): Record<string, string> {
  const session = readSession();

  return session ? { Authorization: `Bearer ${session.accessToken}` } : {};
}

/** Lê o `{ message }` do erro; um proxy no meio pode responder HTML. */
async function errorMessage(response: Response): Promise<string> {
  const fallback = `O serviço de monitoramento respondeu ${response.status}.`;
  const body = await response.json().catch(() => null);

  return (body as { message?: string } | null)?.message || fallback;
}

/**
 * Trata a resposta de erro. 401/403 é a API recusando a sessão: além do erro
 * da chamada, a tela volta para o login.
 */
async function failWith(response: Response): Promise<never> {
  const message = await errorMessage(response);

  if (response.status === 401 || response.status === 403) rejectSession(message);

  throw new Error(message);
}

/** O prazo vigente da sessão, que o monitor devolve em toda resposta válida. */
const SESSION_EXPIRES_HEADER = 'X-Sicop-Session-Expires-At';

/**
 * Usar a tela renova a sessão no servidor; o prazo novo chega por cabeçalho e
 * entra no storage, para o logout automático ser adiado junto.
 */
function noteRenewal(response: Response): void {
  const header = response.headers.get(SESSION_EXPIRES_HEADER);

  if (header) renewSession(Date.parse(header));
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...authHeader(),
        ...init?.headers,
      },
    });
  } catch {
    throw new Error('Serviço de monitoramento fora do ar. Tente novamente.');
  }

  if (!response.ok) return failWith(response);

  noteRenewal(response);

  return (await response.json()) as T;
}

/** `attachment; filename="consumo.xlsx"` → `consumo.xlsx`. */
function attachmentName(disposition: string | null, fallback: string): string {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition ?? '');

  return match ? decodeURIComponent(match[1]) : fallback;
}

/**
 * Baixa o XLSX de um relatório.
 *
 * Por `fetch`, e não por um `<a href>`: o link não manda o cabeçalho
 * `Authorization`, e a API recusa a chamada sem ele. O arquivo vira um Blob, e
 * um link temporário entrega ao navegador com o nome do `Content-Disposition`.
 */
async function downloadReportXlsx(id: number): Promise<void> {
  return downloadXlsx(`/reports/${id}/xlsx`, `relatorio-${id}.xlsx`);
}

/** A planilha da tela de Linhas, com os mesmos filtros da listagem. */
async function exportNumbers({
  operatorCode,
  line,
  masters,
  status,
  groups,
}: Omit<NumbersQuery, 'page'>): Promise<void> {
  const params = new URLSearchParams({ status });
  if (operatorCode) params.set('operator', operatorCode);
  if (line.trim()) params.set('line', line.trim());
  for (const group of groups) params.append('group', group);
  for (const master of masters) params.append('master', master);

  return downloadXlsx(`/numbers/export?${params}`, 'linhas.xlsx');
}

/** Baixa uma planilha da API e a entrega ao navegador; ver `downloadReportXlsx`. */
async function downloadXlsx(path: string, fallbackName: string): Promise<void> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, { headers: authHeader() });
  } catch {
    throw new Error('Serviço de monitoramento fora do ar. Tente novamente.');
  }

  if (!response.ok) return failWith(response);

  noteRenewal(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = attachmentName(response.headers.get('Content-Disposition'), fallbackName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // O clique já entregou o arquivo ao navegador; o endereço não é mais usado.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const monitorApi = {
  list: () => request<ApisState>('/apis'),

  /** Contagens e histórico de execuções da tela inicial. */
  dashboard: () => request<DashboardState>('/dashboard'),

  /** Jobs agendados e a última rodada de cada um (tela de Serviços assíncronos). */
  jobs: () => request<JobsState>('/jobs'),

  /** Página do histórico de execuções; os filtros vão na query porque o servidor pagina. */
  jobRuns: ({ job, status, page }: RunsQuery) => {
    const params = new URLSearchParams({ page: String(page) });
    if (job) params.set('job', job);
    if (status) params.set('status', status);

    return request<ServiceRunsPage>(`/jobs/runs?${params}`);
  },

  /** "Executar agora" — a mesma rodada do cron, marcada como disparo manual. */
  runJob: (job: string) =>
    request<JobsState>(`/jobs/${encodeURIComponent(job)}/run`, { method: 'POST' }),

  operators: () => request<{ operators: OperatorOption[] }>('/operators'),

  /** Página da tela de Linhas; os filtros vão na query porque o servidor pagina. */
  numbers: ({ operatorCode, line, masters, status, groups, page }: NumbersQuery) => {
    const params = new URLSearchParams({ page: String(page), status });
    if (operatorCode) params.set('operator', operatorCode);
    if (line.trim()) params.set('line', line.trim());
    for (const group of groups) params.append('group', group);
    for (const master of masters) params.append('master', master);

    return request<NumbersPage>(`/numbers?${params}`);
  },

  /** Nomes de master presentes na base de linhas — as opções do filtro da tela. */
  lineMasters: () => request<{ masters: string[] }>('/numbers/masters'),

  /** Detalhe de uma linha para o side sheet, com CNPJ, empresa e segmentos. */
  lineDetail: (operator: string, line: string) => {
    const params = new URLSearchParams({ operator, line });
    return request<{ detail: LineDetail }>(`/numbers/detail?${params}`);
  },

  /** Sincroniza o consumo de uma linha; a resposta traz a linha já atualizada. */
  syncNumber: (id: number) => request<LineSyncResult>(`/numbers/${id}/sync`, { method: 'POST' }),

  /**
   * Sincroniza pelo número digitado (o card da tela de serviços). Devolve uma
   * lista porque o mesmo número pode existir nas duas operadoras.
   */
  /**
   * Sincroniza pelo número. Com a operadora, só nela — o botão da tela de
   * Linhas; sem ela, em todas em que o número existe — o card de serviços.
   */
  syncNumberByLine: (line: string, operatorCode?: string) =>
    request<{ results: LineSyncResult[] }>('/numbers/sync', {
      method: 'POST',
      body: JSON.stringify(operatorCode ? { line, operator: operatorCode } : { line }),
    }),

  /** A central de notificações inteira: a lista e o contador do sino. */
  notifications: () => request<NotificationsState>('/notifications'),

  /**
   * "Vi as notificações", ao abrir o sino. `untilId` é a mais recente que
   * estava na tela: o que chegar depois disso continua não lido.
   */
  readNotifications: (untilId: number) =>
    request<NotificationsState>('/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ untilId }),
    }),

  /** Marca uma só — o clique em um item da lista. */
  readNotification: (id: number) =>
    request<NotificationsState>(`/notifications/${id}/read`, { method: 'POST' }),

  /** Contas de uma operadora (a aba da tela de Contas). */
  accounts: (operatorCode: string, status: AccountStatusFilter = 'all') =>
    request<{ accounts: Account[] }>(
      `/accounts?operator=${encodeURIComponent(operatorCode)}&status=${status}`
    ),

  masters: (status: AccountStatusFilter = 'all') =>
    request<{ masters: Master[] }>(`/masters?status=${status}`),

  createMaster: (payload: NewMaster) =>
    request<{ master: Master | null }>('/masters', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateMaster: (id: number, changes: MasterChanges) =>
    request<{ master: Master | null }>(`/masters/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  updateAccount: (id: number, changes: AccountChanges) =>
    request<{ account: Account | null }>(`/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  createAccount: (payload: NewAccount) =>
    request<{ accounts: Account[]; created: Account | null }>('/accounts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  create: (payload: NewApiEndpoint) =>
    request<ApisState & { created: ApiEndpoint | null }>('/apis', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  remove: (id: number) => request<ApisState>(`/apis/${id}`, { method: 'DELETE' }),


  /** Masters e períodos do formulário de relatório. */
  reportOptions: () => request<ReportOptions>('/reports/options'),

  /** Contas de um master; só abre depois de o master ser escolhido. */
  reportAccounts: (masterId: number) =>
    request<{ accounts: ReportAccount[] }>(`/reports/accounts?master=${masterId}`),

  reports: () => request<{ reports: ConsumptionReport[] }>('/reports'),

  /** Pede um relatório. A geração é assíncrona: volta o id e a lista. */
  createReport: (payload: NewReport) =>
    request<{ id: number; reports: ConsumptionReport[] }>('/reports', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** Andamento conta a conta — só faz sentido nos relatórios do portal. */
  reportAccountProgress: (id: number) =>
    request<{ accounts: ReportAccountProgress[] }>(`/reports/${id}/accounts`),

  /** Baixa o XLSX; fora do `request` porque a resposta é um arquivo, não JSON. */
  downloadReportXlsx,

  exportNumbers,

  /** Roda a mesma checagem do cron sob demanda ("Verificar agora"). */
  checkNow: () => request<ApisState>('/apis/check', { method: 'POST' }),

  /**
   * O login: troca o access token do Google pela sessão curta do SICOP.
   *
   * O monitor confere a conta com o Google (Client ID, domínio, allowlist),
   * grava a pessoa em `users` e devolve o token de 15 minutos que todas as
   * outras chamadas apresentam. O token do Google vai no cabeçalho desta
   * chamada e não é usado para mais nada daqui em diante.
   */
  createSession: (googleAccessToken: string) =>
    request<{ token: string; expiresAt: string; user: SessionUser; created: boolean }>(
      '/auth/session',
      { method: 'POST', headers: { Authorization: `Bearer ${googleAccessToken}` } }
    ),

  /** Encerra a sessão no servidor; o storage é limpo por quem chama. */
  logout: async (): Promise<void> => {
    try {
      await fetch(`${BASE_URL}/auth/logout`, { method: 'POST', headers: authHeader() });
    } catch {
      // A sessão expira sozinha em minutos; o logout não precisa falhar.
    }
  },

  /** O `select` de usuário do filtro da auditoria. */
  auditUsers: () => request<{ users: AuditUser[] }>('/users'),

  /** Uma página da auditoria; os filtros vão na query porque o servidor pagina. */
  audit: ({ userId, from, to, page }: AuditQuery) => {
    const params = new URLSearchParams({ page: String(page) });
    if (userId) params.set('user', String(userId));
    if (from) params.set('from', from);
    if (to) params.set('to', to);

    return request<AuditPage>(`/audit?${params}`);
  },
};
